import { createContext, useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
// utils
import { useDispatch } from 'react-redux';
import {
  signInWithEmailAndPassword,
  signOut,
  verifyPasswordResetCode,
  EmailAuthProvider,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  applyActionCode,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '@/features/auth/firebase';
import { GetProfileImg, GetProfile } from 'src/redux/profile';
import { toast } from 'src/components/ui/toast';
import { AuthState } from 'src/constants/auth';
import { apiGet, apiPost } from 'src/lib/api/axios';
import {
  isUserSelfHostedOnly,
  setTenantSelfHostedOnly as setPostHogTenantSelfHostedOnly,
  usePostHog
} from 'src/lib/posthog';

// ----------------------------------------------------------------------

const AuthContext = createContext({
  authState: AuthState.LOADING,
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  method: 'firebase',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  validatePasswordResetCode: () => Promise.resolve(),
  confirmPasswordReset: () => Promise.resolve(),
  verifyEmail: () => Promise.resolve(),
  updatePassword: () => Promise.resolve(),
  updateProfile: () => {},
  resendVerificationEmail: () => Promise.resolve(),
  completeMfa: () => {}
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(AuthState.LOADING);
  const [user, setUser] = useState(null);
  const reduxDispatch = useDispatch();
  const posthog = usePostHog();

  // Derive backward-compatible flags from authState
  const isAuthenticated = authState === AuthState.AUTHENTICATED;
  const isInitialized = authState !== AuthState.LOADING;

  /**
   * Check whether the admin user's MFA claim is present and not expired.
   * Delegates to the backend /auth/mfa/status endpoint which owns the
   * session TTL, so the frontend doesn't duplicate the constant.
   */
  const checkMfaStatus = async () => {
    try {
      const { data } = await apiGet('auth/mfa/status', {
        meta: { globalError: false, context: 'mfa-status' }
      });
      return data.mfa_verified === true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    // The saved session is not available on the first tick of a new tab.
    // Subscribe only after Firebase has read it, or a null user is treated
    // as logged out and the login page flashes before the session returns.
    auth.authStateReady().then(() => {
      if (cancelled) return;
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          if (firebaseUser) {
            // Check if email is verified
            if (!firebaseUser.emailVerified) {
              // Keep user signed in but in unverified state
              // This allows them to request a new verification email
              setUser(null);
              setAuthState(AuthState.EMAIL_UNVERIFIED);
              return;
            }

            // User is logged-in and email verified - fetch profile
            const userData = await reduxDispatch(GetProfile());
            if (userData !== null) {
              // Account images are served by a tenant-scoped endpoint. Resolve the
              // profile first so the request never falls back to the non-existent
              // unscoped /account/image route during auth bootstrap.
              const tenantCode = userData.active_tenant_code ?? userData.tenants?.[0]?.code;
              if (tenantCode) {
                reduxDispatch(GetProfileImg({ suppressError: true, tenantCode }));
              }

              // ORDER MATTERS: setPostHogTenantSelfHostedOnly(true) → identify → setUser.
              // For self-hosted-only tenants the first call opts cloud out of capturing,
              // so the subsequent identify and any in-flight cloudClient writes land as
              // no-ops on cloud and only mirror to self-hosted. JS is single-threaded so
              // nothing can interleave between these statements. Reordering would leak
              // the user's external_id to cloud before the opt-out takes effect.
              //
              // Note: at fresh login `userData.active_tenant_code` is undefined (the URL
              // is still /auth/login when GetProfile runs)
              setPostHogTenantSelfHostedOnly(
                isUserSelfHostedOnly({
                  tenants: userData.tenants
                })
              );
              // active tenant code can change, so we need to remove it from the user data
              // eslint-disable-next-line no-unused-vars
              const { active_tenant_code: _, ...userDataWithoutActiveTenantCode } = userData;
              posthog?.identify(userData.external_id, userDataWithoutActiveTenantCode);

              setUser(userData);

              // Check if admin user needs MFA verification
              const isAdmin = userData.platform_admin === true;
              if (isAdmin) {
                const isMfaValid = await checkMfaStatus();
                if (!isMfaValid) {
                  setAuthState(AuthState.MFA_REQUIRED);
                  return;
                }
              }

              setAuthState(AuthState.AUTHENTICATED);
            } else {
              // Profile fetch failed
              setUser(null);
              setAuthState(AuthState.LOGGED_OUT);
            }
          } else {
            // User is logged-out
            setUser(null);
            setAuthState(AuthState.LOGGED_OUT);
          }
        } catch (error) {
          console.error(error);
          setUser(null);
          setAuthState(AuthState.LOGGED_OUT);
        }
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [reduxDispatch, posthog]);

  const login = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  /**
   * Called after successful MFA verification to transition to authenticated state.
   */
  const completeMfa = useCallback(() => {
    setAuthState(AuthState.AUTHENTICATED);
  }, []);

  const logout = async ({ silent = false } = {}) => {
    try {
      // Revoke MFA claim before signing out so the admin must
      // re-verify MFA on next login. Best-effort — don't block
      // logout if this fails.
      try {
        await apiPost('auth/mfa/revoke', null, {
          meta: { globalError: false, background: true }
        });
      } catch {
        // best-effort
      }

      await signOut(auth);
      setUser(null);
      setAuthState(AuthState.LOGGED_OUT);
      localStorage.removeItem('user');
      posthog?.capture('auth_logout');
      posthog?.reset();
      setPostHogTenantSelfHostedOnly(false);
      if (!silent) {
        toast.success('Logged out successfully.');
      }
    } catch {
      const errorMsg = 'Logout failed. Please try again.';
      toast.error(errorMsg);
      return { error: errorMsg };
    }
  };

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const validatePasswordResetCode = async (oobCode) => {
    const email = await verifyPasswordResetCode(auth, oobCode);
    return { email: email };
  };

  const confirmPasswordReset = async (oobCode, newPassword) => {
    await firebaseConfirmPasswordReset(auth, oobCode, newPassword);
  };

  const verifyEmail = async (oobCode) => {
    await applyActionCode(auth, oobCode);
    await auth.currentUser?.reload();
  };

  const updatePassword = async (oldPassword, newPassword) => {
    const credential = EmailAuthProvider.credential(auth.currentUser.email, oldPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await firebaseUpdatePassword(auth.currentUser, newPassword);
  };

  const updateProfile = () => {};

  const resendVerificationEmail = async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
      toast.success('Verification email sent! Please check your inbox.');
    } else {
      throw new Error('No user is currently signed in.');
    }
  };

  const value = useMemo(
    () => ({
      authState,
      isAuthenticated,
      isInitialized,
      user,
      method: 'firebase',
      login,
      logout,
      resetPassword,
      updateProfile,
      validatePasswordResetCode,
      confirmPasswordReset,
      verifyEmail,
      updatePassword,
      resendVerificationEmail,
      completeMfa
    }),
    // Auth functions are recreated each render but auth state changes infrequently

    [authState, isAuthenticated, isInitialized, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
